/**
 * The course player (plan docs/plans/lms-plan.md 7.2, 7.3, G13, section 13;
 * WP-40): the /lms/courses/:courseId/learn screen. It loads the course
 * (useCourse), then runs the stages (usePlayer) under a stepper: Overview,
 * Slides, Reading, Quiz (or Acknowledge), and Done once complete.
 *
 * The player is the one Learning Center screen that uses the content area
 * edge to edge (G13): its bars span the page, its stages keep a readable
 * measure inside, with the same tokens as every other screen.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { outlineBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useCourse } from '../../hooks/useCourse';
import type { CourseData } from '../../hooks/useCourse';
import { usePlayer } from '../../hooks/usePlayer';
import type { PlayerStage } from '../../hooks/usePlayer';
import { AcknowledgeStage } from './AcknowledgeStage';
import { DoneStage } from './DoneStage';
import { OverviewStage } from './OverviewStage';
import { QuizStage } from './QuizStage';
import { ReadingStage } from './ReadingStage';
import type { SopFileState } from './ReadingStage';
import { SlidesStage } from './SlidesStage';
import { Stepper } from './Stepper';
import { noticeCls } from './styles';

export interface PlayerProps {
  courseId: string;
}

/** 10.1: a completion earns a certificate unless the version says no (best_practices defaults to no). */
function expectsCertificate(data: CourseData): boolean {
  if (typeof data.quizSettings.issue_certificate === 'boolean') return data.quizSettings.issue_certificate;
  return data.course.category !== 'best_practices';
}

function LoadedPlayer({ courseId, data }: { courseId: string; data: CourseData }) {
  const player = usePlayer(courseId, data);
  const { user } = useAuth();
  const signerName = user?.full_name || null;
  const signerEmail = user?.email || null;
  const { course, content, version, assignment } = data;
  // One rule for the "What changed" slide, the overview and the acknowledge stage (usePlayer changeSummaryFor, plan 18.27).
  const changeSummary = player.changeSummary;
  const sources = Array.isArray(course.sources) ? course.sources : [];
  const finalStage: PlayerStage = player.flow === 'acknowledge' ? 'acknowledge' : 'quiz';
  // An SOP course: whether "Open the SOP" opened the file in this visit, from either stage
  // (docs/plans/sop-import-plan.md 4.4 item 3; the reading stage holds its control until it has,
  // or until the server says there is no file this person may open). 'opened' is never undone.
  const [sopFileState, setSopFileState] = useState<SopFileState>('unknown');
  const noteSopFileState = useCallback((state: SopFileState) => setSopFileState((prev) => (prev === 'opened' ? prev : state)), []);
  const noteSopFileOpened = useCallback(() => setSopFileState('opened'), []);

  const complete = useCallback(
    (stage: PlayerStage) => {
      switch (stage) {
        case 'overview':
          return player.hasStarted || player.stage !== 'overview';
        case 'slides':
          return player.slidesDone;
        case 'reading':
          return player.readingDone;
        case 'quiz':
        case 'acknowledge':
        case 'done':
          return Boolean(player.result);
        default:
          return false;
      }
    },
    [player.hasStarted, player.stage, player.slidesDone, player.readingDone, player.result]
  );

  const goTo = player.goToStage;
  const finalLock = player.stageLock(finalStage);

  return (
    <div className="flex min-h-full flex-col" data-testid="lms-player" data-stage={player.stage}>
      <div className="border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-x-3 gap-y-2">
          <Link to={`/lms/courses/${encodeURIComponent(courseId)}`} className={outlineBtnCls()} aria-label="Back to the course page">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Course</span>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100" data-testid="lms-player-title">
              {course.title}
            </h1>
          </div>
          <Stepper
            flow={player.flow}
            stage={player.stage}
            complete={complete}
            lockOf={player.stageLock}
            showDone={Boolean(player.result)}
            onSelect={(s) => goTo(s)}
          />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {player.stage === 'overview' && (
          <OverviewStage
            course={course}
            content={content}
            assignment={assignment}
            versionNo={version.version_no}
            changeSummary={changeSummary}
            sources={sources}
            hasStarted={player.hasStarted}
            onStart={() => goTo('slides')}
          />
        )}
        {player.stage === 'slides' && (
          <SlidesStage
            player={player}
            content={content}
            versionNo={version.version_no}
            changeSummary={changeSummary}
            onFinish={() => goTo('reading')}
          />
        )}
        {player.stage === 'reading' && (
          <ReadingStage
            courseTitle={course.title}
            versionNo={version.version_no}
            content={content}
            sources={sources}
            readingDone={player.readingDone}
            onMarkRead={player.markRead}
            acknowledgeFlow={player.flow === 'acknowledge'}
            target={player.readingTarget}
            onTargetShown={player.clearReadingTarget}
            nextLabel={player.flow === 'acknowledge' ? 'Continue to the acknowledgement' : 'Continue to the quiz'}
            nextDisabledReason={finalLock}
            onNext={() => goTo(finalStage)}
            saveError={player.saveError}
            courseCategory={course.category}
            courseId={courseId}
            courseVersionId={version.id}
            fileState={sopFileState}
            onFileState={noteSopFileState}
          />
        )}
        {player.stage === 'quiz' && (
          <QuizStage
            courseId={courseId}
            userId={user?.id ?? null}
            assignmentId={assignment?.id ?? null}
            lock={player.stageLock('quiz')}
            quizSettings={data.quizSettings}
            sections={content.sections}
            signerName={signerName}
            signerEmail={signerEmail}
            onGoToSlides={() => goTo('slides')}
            onGoToReading={() => goTo('reading')}
            onReview={player.reviewSection}
            onCompleted={player.finish}
          />
        )}
        {player.stage === 'acknowledge' && (
          <AcknowledgeStage
            courseId={courseId}
            courseTitle={course.title}
            readingDone={player.readingDone}
            changeSummary={changeSummary}
            versionNo={version.version_no}
            signerName={signerName}
            signerEmail={signerEmail}
            onGoToReading={() => goTo('reading')}
            onCompleted={player.finish}
            courseCategory={course.category}
            courseVersionId={version.id}
            onFileOpened={noteSopFileOpened}
          />
        )}
        {player.stage === 'done' && player.result && (
          <DoneStage courseId={courseId} courseTitle={course.title} result={player.result} expectsCertificate={expectsCertificate(data)} />
        )}
      </div>
    </div>
  );
}

/** The page heading while there is no course title to show yet (loading, or the load failed). */
const PLAYER_HEADING = 'Course player';

export function Player({ courseId }: PlayerProps) {
  const { data, loading, error, reload } = useCourse(courseId);

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="lms-player-loading">
        <h1 className="sr-only">{PLAYER_HEADING}</h1>
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="sr-only">Loading the course</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8" data-testid="lms-player-error">
        <h1 className="sr-only">{PLAYER_HEADING}</h1>
        <div className={noticeCls('red')} role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0 space-y-2">
            <div>{error || 'The course could not be loaded.'}</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={secondaryBtnCls} onClick={reload}>
                Retry
              </button>
              <Link to="/lms/courses" className={secondaryBtnCls}>
                Back to courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A new version or a site switch mounts a fresh player (its bookmark belongs to that version).
  return <LoadedPlayer key={`${data.version.id}`} courseId={courseId} data={data} />;
}

export default Player;
